import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Tree,
  Row,
  Col,
  Tag,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MenuOutlined,
  DashboardOutlined,
  UserOutlined,
  NotificationOutlined,
  TeamOutlined,
  SettingOutlined,
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { menuService, Menu, MenuWithPermissions } from '../services/menuService';
import type { DataNode } from 'antd/es/tree';

const { Option } = Select;

// 아이콘 맵핑
const iconMap: Record<string, React.ReactNode> = {
  DashboardOutlined: <DashboardOutlined />,
  UserOutlined: <UserOutlined />,
  NotificationOutlined: <NotificationOutlined />,
  TeamOutlined: <TeamOutlined />,
  SettingOutlined: <SettingOutlined />,
  MenuOutlined: <MenuOutlined />,
  LockOutlined: <LockOutlined />,
};

const MenuManagement: React.FC = () => {
  const [menus, setMenus] = useState<MenuWithPermissions[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null);
  const [form] = Form.useForm();
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    loadMenus();
  }, []);

  const loadMenus = async () => {
    setLoading(true);
    try {
      const data = await menuService.getMenus(true);
      setMenus(data);
    } catch (error) {
      message.error('메뉴 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingMenu(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (menu: Menu) => {
    setEditingMenu(menu);
    form.setFieldsValue({
      name: menu.name,
      path: menu.path,
      icon: menu.icon,
      parent_id: menu.parent_id,
      sort_order: menu.sort_order,
      is_active: menu.is_active,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await menuService.deleteMenu(id);
      message.success('메뉴가 삭제되었습니다.');
      loadMenus();
    } catch (error) {
      message.error('메뉴 삭제에 실패했습니다.');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingMenu) {
        await menuService.updateMenu(editingMenu.id, values);
        message.success('메뉴가 수정되었습니다.');
      } else {
        await menuService.createMenu(values);
        message.success('메뉴가 생성되었습니다.');
      }
      setModalVisible(false);
      loadMenus();
    } catch (error) {
      message.error('작업 중 오류가 발생했습니다.');
    }
  };

  const handleMoveUp = async (menu: Menu, parentMenus: Menu[]) => {
    const currentIndex = parentMenus.findIndex(m => m.id === menu.id);
    if (currentIndex > 0) {
      const updatedMenus = [...parentMenus];
      const temp = updatedMenus[currentIndex - 1];
      updatedMenus[currentIndex - 1] = updatedMenus[currentIndex];
      updatedMenus[currentIndex] = temp;

      const updates = updatedMenus.map((m, index) => ({
        id: m.id,
        sort_order: index + 1,
      }));

      await menuService.updateMenuOrder(updates);
      loadMenus();
    }
  };

  const handleMoveDown = async (menu: Menu, parentMenus: Menu[]) => {
    const currentIndex = parentMenus.findIndex(m => m.id === menu.id);
    if (currentIndex < parentMenus.length - 1) {
      const updatedMenus = [...parentMenus];
      const temp = updatedMenus[currentIndex + 1];
      updatedMenus[currentIndex + 1] = updatedMenus[currentIndex];
      updatedMenus[currentIndex] = temp;

      const updates = updatedMenus.map((m, index) => ({
        id: m.id,
        sort_order: index + 1,
      }));

      await menuService.updateMenuOrder(updates);
      loadMenus();
    }
  };

  // 트리 데이터 생성
  const buildTreeData = (menus: MenuWithPermissions[]): DataNode[] => {
    return menus.map(menu => ({
      title: (
        <span>
          {iconMap[menu.icon || 'MenuOutlined']} {menu.name}
          {!menu.is_active && <Tag color="red" style={{ marginLeft: 8 }}>비활성</Tag>}
        </span>
      ),
      key: menu.id,
      children: menu.children ? buildTreeData(menu.children) : undefined,
    }));
  };

  // 플랫 리스트 생성
  const flattenMenus = (menus: MenuWithPermissions[], parent?: MenuWithPermissions): Menu[] => {
    const result: Menu[] = [];
    menus.forEach(menu => {
      result.push(menu);
      if (menu.children) {
        result.push(...flattenMenus(menu.children, menu));
      }
    });
    return result;
  };

  const columns = [
    {
      title: '메뉴명',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Menu) => (
        <Space>
          {iconMap[record.icon || 'MenuOutlined']}
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: '경로',
      dataIndex: 'path',
      key: 'path',
    },
    {
      title: '순서',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 100,
      align: 'center' as const,
    },
    {
      title: '상태',
      key: 'status',
      width: 150,
      render: (_: any, record: Menu) => (
        <Space>
          {record.is_active ? (
            <Tag color="green">활성</Tag>
          ) : (
            <Tag color="red">비활성</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '작업',
      key: 'action',
      width: 200,
      render: (_: any, record: Menu) => {
        const parentMenus = menus.filter(m => m.parent_id === record.parent_id);
        const currentIndex = parentMenus.findIndex(m => m.id === record.id);
        
        return (
          <Space>
            <Button
              icon={<UpOutlined />}
              size="small"
              disabled={currentIndex === 0}
              onClick={() => handleMoveUp(record, parentMenus)}
            />
            <Button
              icon={<DownOutlined />}
              size="small"
              disabled={currentIndex === parentMenus.length - 1}
              onClick={() => handleMoveDown(record, parentMenus)}
            />
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            >
              수정
            </Button>
            <Popconfirm
              title="이 메뉴를 삭제하시겠습니까?"
              description="하위 메뉴도 함께 삭제됩니다."
              onConfirm={() => handleDelete(record.id)}
              okText="삭제"
              cancelText="취소"
            >
              <Button icon={<DeleteOutlined />} size="small" danger>
                삭제
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <h1>메뉴 관리</h1>
      
      <Card style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            메뉴 추가
          </Button>
        </div>

        <Row gutter={16}>
          <Col span={8}>
            <Card title="메뉴 구조" size="small">
              <Tree
                treeData={buildTreeData(menus)}
                selectedKeys={selectedKeys}
                onSelect={setSelectedKeys}
                defaultExpandAll
              />
            </Card>
          </Col>
          <Col span={16}>
            <Table
              columns={columns}
              dataSource={flattenMenus(menus)}
              rowKey="id"
              loading={loading}
              pagination={false}
            />
          </Col>
        </Row>
      </Card>

      <Modal
        title={editingMenu ? '메뉴 수정' : '메뉴 추가'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
            sort_order: 0,
          }}
        >
          <Form.Item
            name="name"
            label="메뉴명"
            rules={[{ required: true, message: '메뉴명을 입력해주세요' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="path"
            label="경로"
            rules={[{ required: true, message: '경로를 입력해주세요' }]}
          >
            <Input placeholder="/dashboard" />
          </Form.Item>

          <Form.Item name="icon" label="아이콘">
            <Select placeholder="아이콘 선택">
              <Option value="DashboardOutlined">
                <DashboardOutlined /> 대시보드
              </Option>
              <Option value="UserOutlined">
                <UserOutlined /> 사용자
              </Option>
              <Option value="NotificationOutlined">
                <NotificationOutlined /> 알림
              </Option>
              <Option value="TeamOutlined">
                <TeamOutlined /> 팀
              </Option>
              <Option value="SettingOutlined">
                <SettingOutlined /> 설정
              </Option>
              <Option value="MenuOutlined">
                <MenuOutlined /> 메뉴
              </Option>
              <Option value="LockOutlined">
                <LockOutlined /> 보안
              </Option>
            </Select>
          </Form.Item>

          <Form.Item name="parent_id" label="상위 메뉴">
            <Select placeholder="최상위 메뉴" allowClear>
              {flattenMenus(menus).map(menu => (
                <Option key={menu.id} value={menu.id}>
                  {menu.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="sort_order" label="정렬 순서">
            <InputNumber min={0} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="is_active" label="활성화" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
            </Col>
          </Row>


          <Form.Item>
            <Space style={{ float: 'right' }}>
              <Button onClick={() => setModalVisible(false)}>취소</Button>
              <Button type="primary" htmlType="submit">
                {editingMenu ? '수정' : '추가'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuManagement;